<template>
    <div class="divide-y divide-gray-200">
        <div class="py-6 px-4 sm:p-6 lg:pb-8">
            
            <h2 class="text-lg leading-6 font-medium text-gray-900">Rate Card</h2>
            <p class="mt-1 text-sm text-gray-500">
                Make it clear the prices your students can expect to pay.
            </p>
            


            <label for="name" class="block text-sm font-medium text-gray-700 mt-4">
                Do you have a fixed hourly rate or a range depending on subject and/or level?
            </label>
            <tti-button-group :options="[{ label: 'Fixed', value: true }, { label: 'Range', value: false }]"
                            :value="isFixedRate"
                            @click="isFixedRate = $event"
                            class="mt-2">
            </tti-button-group>
            

            <div v-if="isFixedRate" class="flex-grow space-y-6 grid grid-cols-12 mt-4">
                    
                <div class="col-span-12 space-y-1">
                    <label for="hourlyrate" class="block text-sm font-medium text-gray-700">
                        Fixed Rate
                    </label>
                    <div class="flex items-center space-x-2">
                        <span>£</span>
                        <div class="rounded-md shadow-sm">
                            <input type="text" v-model.number="fromRate" @blur="onRateBlur" name="hourlyrate" id="hourlyrate" inputmode="numeric" class="focus:ring-light-blue-500 focus:border-light-blue-500 flex-grow block w-full min-w-0 rounded sm:text-sm border-gray-300 placeholder-gray-400 max-w-20" placeholder="25" />
                        </div>
                        <span class="flex-shrink-0">per hour</span>
                    </div>
                    <p v-if="$v.fromRate.$dirty && !$v.fromRate.minValue" class="text-sm text-red-600">The minimum rate is £{{ londonLivingWage }} – in line with the London Living Wage.</p>
                </div>
            </div>

            <div v-else class="flex-grow space-y-2 grid grid-cols-12 mt-4">
                    
                <div class="col-span-12">
                    <label for="fromrate" class="block text-sm font-medium text-gray-700">
                        From
                    </label>
                    <div class="mt-1 flex items-center space-x-2">
                        <span>£</span>
                        <div class="rounded-md shadow-sm">
                            <input type="text" v-model.number="fromRate" @blur="onRateBlur" name="fromrate" id="fromrate" inputmode="numeric" class="focus:ring-light-blue-500 focus:border-light-blue-500 flex-grow block w-full min-w-0 rounded sm:text-sm border-gray-300 placeholder-gray-400 max-w-20" placeholder="25" />
                        </div>
                        <span class="flex-shrink-0">per hour</span>
                    </div>
                    <p v-if="$v.fromRate.$dirty && !$v.fromRate.minValue" class="text-sm text-red-600">The minimum rate is £{{ londonLivingWage }} – in line with the London Living Wage.</p>
                </div>
                <div class="col-span-12">
                    <label for="torate" class="block text-sm font-medium text-gray-700">
                        To
                    </label>
                    <div class="mt-1 flex items-center space-x-2">
                        <span>£</span>
                        <div class="rounded-md shadow-sm">
                            <input type="text" v-model.number="toRate" @blur="onRateBlur" name="torate" id="torate" inputmode="numeric" class="focus:ring-light-blue-500 focus:border-light-blue-500 flex-grow block w-full min-w-0 rounded sm:text-sm border-gray-300 placeholder-gray-400 max-w-20" placeholder="40" />
                        </div>
                        <span class="flex-shrink-0">per hour</span>
                    </div>
                </div>
                <p v-if="$v.toRate.$dirty && !$v.toRate.minValue" class="text-sm text-red-600">To rate must not be smaller than From rate.</p>
                
            </div>

            <button type="button" v-if="fromRate || toRate" @click="fromRate = null; toRate = null; onRateBlur();" class="text-sm text-gray-500 underline">Clear Rates</button>


            <form @submit.prevent="saveRateInfo" class="mt-6">
                <label for="rateinfo" class="block text-sm font-medium text-gray-700">Additional Rate Information</label>
                <textarea v-model="additionalRateInfo" id="rateinfo" name="rateinfo" rows="3" class="shadow-sm focus:ring-light-blue-500 focus:border-light-blue-500 mt-2 block w-full sm:text-sm border-gray-300 rounded-md"></textarea>
                <div class="flex flex-col flex-col-reverse sm:flex sm:flex-row sm:justify-between sm:space-x-3 sm:items-start">
                    <p class="mt-2 text-sm text-gray-500">For more detailed information about your rates, plus any other costs that may be incurred. For example, you may wish to explain about additional travel fees beyond a certain distance, or you may mention that the first lesson is offered at a reduced rate.</p>
                    <div v-if="additionalRateInfo !== $store.state.user.AdditionalRateInformation" class="mt-2 space-x-2 flex-shrink-0 sm:flex sm:flex-row-reverse sm:space-x-reverse">
                        <tti-button type="submit" size="xsmall" :loading="savingAdditionalRateInfo" :disabled="savingAdditionalRateInfo">Save</tti-button>
                        <tti-button @click="additionalRateInfo = $store.state.user.AdditionalRateInformation" size="xsmall" theme="outline" :disabled="savingAdditionalRateInfo">Cancel</tti-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
<script>

import debounce from 'debounce'

export default {
    components: {
        'tti-button': () => import('./TailwindButton'),
        'tti-button-group': () => import('./TailwindButtonGroup')
    },
    data() {
        return {
            londonLivingWage: 10.85,
            isFixedRate: true,
            fromRate: null,
            toRate: null,
            additionalRateInfo: null,
            savingAdditionalRateInfo: false
        }
    },
    validations: {
        fromRate: {
            minValue: function(value) {
                return value === null || value > this.londonLivingWage;
            }
        },
        toRate: {
            minValue: function(value) {
                return value === null || value >= this.fromRate;
            }
        }
    },
    computed: {
        lessThanLondonLivingWage() {
            return this.fromRate < this.londonLivingWage;
        }
    },
    watch: {
        isFixedRate: function(newVal) {
            if (newVal) {
                this.toRate = this.fromRate;
                this.onRateBlur();
            }
        },
        fromRate: debounce(function(newVal) {
            if (this.isFixedRate)
                this.toRate = newVal;

            if (newVal > this.toRate)
                this.toRate = newVal;
        }, 300),
        toRate: debounce(function(newVal) {
            if (newVal < this.fromRate)
                this.fromRate = newVal;
        }, 300)
    },
    methods: {
        async onRateBlur() {
            this.$v.$touch();

            if ((this.fromRate === this.$store.state.user.RateFrom && 
                this.toRate === this.$store.state.user.RateTo) 
                || this.$v.$invalid)
                return;

            await this.$store.dispatch('updateUser', {
                RateFrom: this.fromRate,
                RateTo: this.toRate
            });

            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
            this.$v.$reset();
        },
        async saveRateInfo() {
            this.savingAdditionalRateInfo = true;
            await this.$store.dispatch('updateUser', { AdditionalRateInformation: this.additionalRateInfo });

            this.savingAdditionalRateInfo = false
            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved' });
        }
    },
    mounted() {
        this.isFixedRate = this.$store.state.user.RateFrom === this.$store.state.user.RateTo;
        this.fromRate = this.$store.state.user.RateFrom;
        this.toRate = this.$store.state.user.RateTo;
        this.additionalRateInfo = this.$store.state.user.AdditionalRateInformation;
    }
}
</script>